import type { GameAdConfig } from '../types';

export const GAME_AD_CONFIG_QUERY_KEY = ['gameAdConfig'] as const;

export const DEFAULT_GAME_AD_CONFIG: GameAdConfig = Object.freeze({
  version: 0,
  aggression: 0,
  rolloutPercent: 0,
  experimentId: 'game-ads-v1',
  mode: 'test',
  rewardedEnabled: true,
  interstitialEnabled: true,
  bannerEnabled: true,
  interstitialQuestionInterval: null,
  interstitialCooldownSeconds: null,
  maxInterstitialsPerSession: 20,
  maxRewardedPerSession: 3,
});

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function boundedInt(value: unknown, fallback: number, min: number, max: number): number {
  if (!Number.isInteger(value) || (value as number) < min || (value as number) > max)
    return fallback;
  return value as number;
}

function optionalBoundedInt(value: unknown, min: number, max: number): number | null {
  if (value === null) return null;
  if (!Number.isInteger(value) || (value as number) < min || (value as number) > max) {
    return null;
  }
  return value as number;
}

/** Invalid or non-test configuration always becomes the production-safe hard-off config. */
export function parseGameAdConfig(value: unknown): GameAdConfig {
  if (!isRecord(value) || value.mode !== 'test') return DEFAULT_GAME_AD_CONFIG;

  const experimentId = typeof value.experimentId === 'string' ? value.experimentId.trim() : '';
  if (
    !experimentId ||
    experimentId.length > 80 ||
    !Number.isInteger(value.version) ||
    (value.version as number) < 0 ||
    (value.version as number) > 2_147_483_647 ||
    !Number.isInteger(value.aggression) ||
    (value.aggression as number) < 0 ||
    (value.aggression as number) > 100 ||
    !Number.isInteger(value.rolloutPercent) ||
    (value.rolloutPercent as number) < 0 ||
    (value.rolloutPercent as number) > 100 ||
    typeof value.rewardedEnabled !== 'boolean' ||
    typeof value.interstitialEnabled !== 'boolean' ||
    typeof value.bannerEnabled !== 'boolean' ||
    !(
      value.interstitialQuestionInterval === null ||
      (Number.isInteger(value.interstitialQuestionInterval) &&
        (value.interstitialQuestionInterval as number) >= 1 &&
        (value.interstitialQuestionInterval as number) <= 1_000)
    ) ||
    !(
      value.interstitialCooldownSeconds === null ||
      (Number.isInteger(value.interstitialCooldownSeconds) &&
        (value.interstitialCooldownSeconds as number) >= 1 &&
        (value.interstitialCooldownSeconds as number) <= 86_400)
    ) ||
    !Number.isInteger(value.maxInterstitialsPerSession) ||
    (value.maxInterstitialsPerSession as number) < 0 ||
    (value.maxInterstitialsPerSession as number) > 100 ||
    !Number.isInteger(value.maxRewardedPerSession) ||
    (value.maxRewardedPerSession as number) < 0 ||
    (value.maxRewardedPerSession as number) > 20
  ) {
    return DEFAULT_GAME_AD_CONFIG;
  }

  return Object.freeze({
    version: boundedInt(value.version, DEFAULT_GAME_AD_CONFIG.version, 0, 2_147_483_647),
    aggression: boundedInt(value.aggression, 0, 0, 100),
    rolloutPercent: boundedInt(value.rolloutPercent, 0, 0, 100),
    experimentId,
    mode: 'test',
    rewardedEnabled: value.rewardedEnabled,
    interstitialEnabled: value.interstitialEnabled,
    bannerEnabled: value.bannerEnabled,
    interstitialQuestionInterval: optionalBoundedInt(value.interstitialQuestionInterval, 1, 1_000),
    interstitialCooldownSeconds: optionalBoundedInt(value.interstitialCooldownSeconds, 1, 86_400),
    maxInterstitialsPerSession: boundedInt(
      value.maxInterstitialsPerSession,
      DEFAULT_GAME_AD_CONFIG.maxInterstitialsPerSession,
      0,
      100
    ),
    maxRewardedPerSession: boundedInt(
      value.maxRewardedPerSession,
      DEFAULT_GAME_AD_CONFIG.maxRewardedPerSession,
      0,
      20
    ),
  });
}

export async function loadGameAdConfig(): Promise<GameAdConfig> {
  const { supabase } = await import('@/lib/supabase');
  const { data, error } = await supabase.rpc('get_game_ad_config');
  if (error) throw error;
  return parseGameAdConfig(data);
}
